import * as d3 from "d3";
import { useEffect, useRef } from "react";

const DonutChart = ({
  data = [],
  width = 400,
  height = 400,
  cityName = "",
}) => {
  const ref = useRef();

  // Internal dummy slice values
  const donutValues = [
    { label: "Area", value: 500 },
    { label: "Population", value: 500 },
    { label: "Density", value: 500 },
    { label: "Wards", value: 500 },
  ];

  useEffect(() => {
    if (!data.length) return;

    const max = d3.max(donutValues.map((d) => d.value));
    const minThreshold = 30; // Minimum threshold value

    // Normalize data for visual representation
    const normalizedData = (dataelement) =>
      dataelement.map((d) => ({
        ...d,
        value: Math.max((d.value * 50) / max, minThreshold),
      }));

    const margin = 20;
    const radius = Math.min(width, height) / 2 - margin;

    const svg = d3
      .select(ref.current)
      .attr("width", width)
      .attr("height", height)
      .append("g")
      .attr("transform", `translate(${width / 2}, ${height / 2})`);

    svg
      .append("defs")
      .append("filter")
      .attr("id", "shadow")
      .append("feDropShadow")
      .attr("dx", 3)
      .attr("dy", 3)
      .attr("stdDeviation", 5)
      .attr("flood-color", "#888");

    svg
      .append("circle")
      .attr("r", radius * 0.85)
      .style("fill", "#fff")
      .style("filter", "url(#shadow)");

    const pie = d3
      .pie()
      .value((d) => d.value)
      .sort(null);
    const arc = d3
      .arc()
      .innerRadius(radius * 0.4)
      .outerRadius(radius * 0.8)
      .cornerRadius(16);

    const outerArc = d3
      .arc()
      .innerRadius(radius * 0.9)
      .outerRadius(radius * 0.9);

    const donutData = normalizedData(
      donutValues.map((dv) => {
        const match = data.find((d) => d.indicator === dv.label);
        return {
          label: dv.label,
          value: dv.value,
          color: match?.color || "#ccc",
          text: match?.value || "",
        };
      })
    );
    svg
      .selectAll("path")
      .data(pie(donutData))
      .join("path")
      .attr("d", arc)
      .attr("fill", (d) => d.data.color)
      .attr("stroke", "white")
      .style("strokeWidth", "8px");

    svg
      .selectAll("polyline")
      .data(pie(donutData))
      .join("polyline")
      .attr("points", (d) => {
        const pos = outerArc.centroid(d);
        const midAngle = (d.startAngle + d.endAngle) / 2;
        const x = radius * 1.3 * (midAngle < Math.PI ? 1 : -1);
        return [arc.centroid(d), outerArc.centroid(d), [x, pos[1]]];
      })
      .style("fill", "none")
      .style("stroke", "gray")
      .style("strokeWidth", "1px")
      .style("stroke-dasharray", "3,3");

    // Upper Text (Value + Unit)
    svg
      .selectAll(".upper-label")
      .data(pie(donutData))
      .join("text")
      .text((d) => d.data.text)
      .attr("transform", (d) => {
        const pos = outerArc.centroid(d);
        const midAngle = (d.startAngle + d.endAngle) / 2;
        const x = radius * 0.88 * (midAngle < Math.PI ? 1 : -1);
        return `translate(${x}, ${pos[1] - 10})`;
      })
      .style("text-anchor", (d) => {
        const midAngle = (d.startAngle + d.endAngle) / 2;
        return midAngle < Math.PI ? "start" : "end";
      })
      .style("font-size", window.innerWidth >= 680 ? "20px" : "14px")
      .style("font-weight", "bold")
      .style("fill", "#333");

    // Lower Text (Label)
    svg
      .selectAll(".lower-label")
      .data(pie(donutData))
      .join("text")
      .text((d) => d.data.label)
      .attr("transform", (d) => {
        const pos = outerArc.centroid(d);
        const midAngle = (d.startAngle + d.endAngle) / 2;
        const x = radius * 0.8 * (midAngle < Math.PI ? 1 : -1);
        return `translate(${x}, ${pos[1] + 15})`;
      })
      .style("text-anchor", (d) => {
        const midAngle = (d.startAngle + d.endAngle) / 2;
        return midAngle < Math.PI ? "start" : "end";
      })
      .style("font-size", window.innerWidth >= 680 ? "16px" : "14px")
      .style("fill", "#333");

    // Center City Text
    svg
      .append("text")
      .attr("text-anchor", "middle")
      .attr("dy", "0.35em")
      .style("font-size", "16px")
      .style("font-weight", "bold")
      .style("fill", "#2F4B7C")
      .text(cityName);

    return () => {
      d3.select(ref.current).selectAll("*").remove();
    };
  }, [data, width, height, cityName]);

  return (
    <div id="overview-donut-chart">
      <svg
        ref={ref}
        viewBox={`0 0 ${width} ${height}`}
        className="block w-full h-auto"
      ></svg>
    </div>
  );
};

export default DonutChart;
